import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import TicketQR from '../components/TicketQR';
import { fmtDate, isPast, fullName, money } from '../events/util';

const API = process.env.REACT_APP_API_URL || '';

// NO hay login con Google acá, a propósito. Esta pantalla vive en el subdominio de
// cada boliche (<slug>.tukipro.com) y Google exige declarar CADA origen a mano en la
// consola: no acepta comodines. Con un boliche era un trámite; con cientos es
// imposible, y cada alta nueva quedaría con el login roto hasta que alguien entre a
// la consola. El que ya se había registrado con Google entra con "Ingresar con
// código", que valida contra el email y no pide contraseña.

export default function MiCuenta() {
  const { slug } = useParams();
  const tokenKey = `tk_buyer_${slug}`;
  const [token, setToken] = useState(() => localStorage.getItem(tokenKey));
  const [data, setData] = useState(null);

  // Se expone para volver a pedir los datos después de un canje: el saldo y el stock
  // cambian y la pantalla tiene que mostrarlo sin recargar a mano.
  const cargar = useCallback(() => {
    if (!token) return;
    fetch(`${API}/api/public/account/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r)).then(setData)
      .catch(() => { localStorage.removeItem(tokenKey); setToken(null); });
  }, [token]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  const onAuth = (t) => { localStorage.setItem(tokenKey, t); setToken(t); };
  const logout = () => { localStorage.removeItem(tokenKey); setToken(null); setData(null); };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '40px 20px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/assets/tukipass-icon.png" alt="" style={{ height: 40 }} />
          <div className="lb-eyebrow" style={{ marginTop: 8 }}>Mi cuenta</div>
        </div>
        {!token ? <Login slug={slug} onAuth={onAuth} /> : !data ? <div style={{ color: 'var(--ink-600)', textAlign: 'center' }}>Cargando…</div> : <Dashboard data={data} cargar={cargar} onLogout={logout} token={token} />}
      </div>
    </div>
  );
}

function Login({ slug, onAuth }) {
  const [mode, setMode] = useState('login'); // login | register | code
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [lastname, setLastname] = useState('');
  const [code, setCode] = useState('');
  const [codeStage, setCodeStage] = useState('email');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const post = async (path, body) => {
    const r = await fetch(`${API}/api/public/account/${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug, ...body }),
    });
    const d = await r.json().catch(() => ({}));
    return { ok: r.ok, d };
  };

  // PUERTA ÚNICA: el mismo formulario sirve para comprador y para el equipo
  // (dueño, admin, socio, RRPP). El servidor resuelve contra los dos sistemas de
  // cuentas y dice de cuál es; acá solo se manda a cada uno a su panel.
  async function doLogin() {
    setErr(''); setLoading(true);
    const { ok, d } = await post('access', { email, password });
    setLoading(false);
    if (!ok) { setErr(d.error || 'No se pudo ingresar'); return; }

    if (d.kind === 'staff') {
      // Sesión del panel: se guarda con la misma clave que usa /admin, así el
      // usuario entra ya logueado y no tiene que volver a identificarse.
      // El panel guarda solo el token (los datos del usuario los pide a /auth/me),
      // y este token trae el mismo contenido que el de /api/auth/login.
      try { localStorage.setItem('tk_token', d.token); } catch {}
      const destino = {
        owner: '/admin/owner',
        admin: '/admin/panel',
        socio: '/admin/socio',
        rrpp: '/admin/rrpp',
      }[d.user?.role] || '/admin';
      window.location.href = destino;
      return;
    }
    onAuth(d.token);   // comprador: se queda en su cuenta
  }
  async function doRegister() {
    setErr('');
    if (!name.trim() || !lastname.trim()) { setErr('Nombre y apellido obligatorios'); return; }
    if (password.length < 6) { setErr('La contraseña debe tener al menos 6 caracteres'); return; }
    setLoading(true);
    const { ok, d } = await post('register', { email, password, name: name.trim(), lastname: lastname.trim() });
    setLoading(false);
    if (!ok) { setErr(d.error || 'No se pudo crear la cuenta'); return; }
    onAuth(d.token);
  }
  async function requestCode() {
    setErr(''); setLoading(true);
    const { ok, d } = await post('request-code', { email });
    setLoading(false);
    if (!ok) { setErr(d.error || 'Error'); return; }
    setCodeStage('sent');
    setMsg(d.sent ? `Te enviamos un código a ${email}` : (d.dev_code ? `Código (dev): ${d.dev_code}` : 'Revisá tu email'));
  }
  async function verifyCode() {
    setErr(''); setLoading(true);
    const { ok, d } = await post('verify', { email, code });
    setLoading(false);
    if (!ok) { setErr(d.error || 'Código inválido'); return; }
    onAuth(d.token);
  }

  return (
    <div className="lb-card" style={{ padding: '28px 26px', maxWidth: 400, margin: '0 auto' }}>
      {mode === 'login' && (
        <>
          {/* Es la puerta de TODOS: comprador y equipo. Se aclara porque si no, el
              que trabaja en el boliche no sabe que puede entrar por acá. */}
          <div style={{ fontSize: 12.5, color: 'var(--ink-600)', marginBottom: 14, lineHeight: 1.5, textAlign: 'center' }}>
            Entrá con tu email, seas <b style={{ color: 'var(--ink-800)' }}>cliente</b> o parte del <b style={{ color: 'var(--ink-800)' }}>equipo</b> del boliche. Te llevamos a donde corresponda.
          </div>
          <input className="lb-input" type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} />
          <input className="lb-input" type="password" placeholder="Contraseña" value={password} onChange={e => setPassword(e.target.value)} style={{ marginTop: 10 }} onKeyDown={e => e.key === 'Enter' && doLogin()} />
          {err && <div className="lb-err" style={{ marginTop: 8 }}>{err}</div>}
          <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 14 }} onClick={doLogin} disabled={loading || !email || !password}>{loading ? 'Ingresando…' : 'Ingresar'}</button>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, fontSize: 13 }}>
            <button className="lb-link" onClick={() => { setErr(''); setMode('register'); }}>Crear cuenta</button>
            <button className="lb-link" onClick={() => { setErr(''); setCodeStage('email'); setMode('code'); }}>Ingresar con código</button>
          </div>
          {/* El que se habia registrado con Google NO tiene contraseña: si no se le
              dice, prueba una y otra vez con cualquiera y termina escribiendo al
              boliche. El código por email lo deja entrar igual. */}
          <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 12, textAlign: 'center', lineHeight: 1.5 }}>
            ¿Antes entrabas con Google? Usá <b style={{ color: 'var(--ink-800)' }}>Ingresar con código</b>: te llega uno por email y entrás con la misma cuenta de siempre.
          </div>
        </>
      )}

      {mode === 'register' && (
        <>
          <div style={{ display: 'flex', gap: 10 }}>
            <input className="lb-input" placeholder="Nombre" value={name} onChange={e => setName(e.target.value)} />
            <input className="lb-input" placeholder="Apellido" value={lastname} onChange={e => setLastname(e.target.value)} />
          </div>
          <input className="lb-input" type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} style={{ marginTop: 10 }} />
          <input className="lb-input" type="password" placeholder="Contraseña (mín. 6)" value={password} onChange={e => setPassword(e.target.value)} style={{ marginTop: 10 }} />
          {err && <div className="lb-err" style={{ marginTop: 8 }}>{err}</div>}
          <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 14 }} onClick={doRegister} disabled={loading || !email || !password}>{loading ? 'Creando…' : 'Crear cuenta'}</button>
          <div style={{ marginTop: 14, fontSize: 13, textAlign: 'center' }}>
            <button className="lb-link" onClick={() => { setErr(''); setMode('login'); }}>Ya tengo cuenta · Ingresar</button>
          </div>
        </>
      )}

      {mode === 'code' && (
        <>
          {codeStage === 'email' ? (
            <>
              <p style={{ color: 'var(--ink-700)', fontSize: 14, marginTop: 0 }}>Te enviamos un código de acceso por email. Sirve si olvidaste tu contraseña o si nunca tuviste una porque entrabas con Google.</p>
              <input className="lb-input" type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} />
              {err && <div className="lb-err" style={{ marginTop: 8 }}>{err}</div>}
              <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 14 }} onClick={requestCode} disabled={loading || !email}>{loading ? 'Enviando…' : 'Enviar código →'}</button>
            </>
          ) : (
            <>
              <p style={{ color: '#2ecc71', fontSize: 13 }}>{msg}</p>
              <input className="lb-input" inputMode="numeric" placeholder="Código de 6 dígitos" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.3em' }} />
              {err && <div className="lb-err" style={{ marginTop: 8 }}>{err}</div>}
              <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 14 }} onClick={verifyCode} disabled={loading || code.length < 6}>{loading ? 'Verificando…' : 'Ingresar'}</button>
            </>
          )}
          <div style={{ marginTop: 14, fontSize: 13, textAlign: 'center' }}>
            <button className="lb-link" onClick={() => { setErr(''); setMode('login'); }}>← Volver al login</button>
          </div>
        </>
      )}
    </div>
  );
}

function Dashboard({ data, onLogout, token, cargar }) {
  const [openTicket, setOpenTicket] = useState(null);
  const [showHist, setShowHist] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [variantePick, setVariantePick] = useState({});  // { catalogId: 'Talle M' }
  const [canjeando, setCanjeando] = useState(null);      // id del premio en curso
  const [canjeErr, setCanjeErr] = useState(null);        // { id, msg }
  const [canjeado, setCanjeado] = useState(null);        // { codigo, premio, points }

  async function deleteAccount() {
    if (!window.confirm('¿Borrar tu cuenta y tus datos personales de este boliche?\n\nImportante: tus entradas ya compradas SIGUEN SIENDO VÁLIDAS para entrar (el QR no se anula), pero se quitan tu nombre y datos. Se borran tu cuenta y tus puntos. No se puede deshacer.')) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API}/api/public/account/me`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      alert('Listo. Se borraron tus datos personales de este boliche. Tus QR ya comprados siguen sirviendo para entrar.');
      onLogout();
    } catch { alert('No se pudo borrar. Probá de nuevo o escribí a privacidad@tukipro.com'); } finally { setDeleting(false); }
  }

  const firstName = (data.name || '').trim().split(' ')[0];
  const points = data.points || 0;

  // CANJE DESDE LA CUENTA: descuenta los puntos en el momento y devuelve un código que la
  // persona presenta en la barra. Se confirma antes, porque es plata: una vez canjeado, el
  // premio hay que ir a buscarlo.
  async function canjear(premio) {
    const variantes = (Array.isArray(premio.variants) ? premio.variants : []).filter(v => v.stock == null || v.stock > 0);
    let variante = null;
    if (variantes.length) {
      variante = variantePick[premio.id] || '';
      if (!variante) { setCanjeErr({ id: premio.id, msg: 'Elegí una opción antes de canjear' }); return; }
    }
    const detalle = variante ? `${premio.name} · ${variante}` : premio.name;
    const aviso = premio.tipo === 'cupon'
      ? `Se te descuentan ahora y te damos un cupón de ${premio.descuento_pct || 100}% off para usar al comprar tu entrada. Sirve una sola vez.`
      : 'Se te descuentan ahora y te damos un código para retirarlo en el boliche.';
    if (!window.confirm(`¿Canjear ${detalle} por ${premio.points_required} puntos?\n\n${aviso}`)) return;
    setCanjeando(premio.id); setCanjeErr(null);
    try {
      const r = await fetch(`${API}/api/public/account/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ catalog_id: premio.id, variant_label: variante }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setCanjeErr({ id: premio.id, msg: d.error || 'No se pudo canjear' }); return; }
      setCanjeado({ ...d, premio: detalle });
      cargar();
    } catch { setCanjeErr({ id: premio.id, msg: 'No se pudo canjear. Probá de nuevo.' }); }
    finally { setCanjeando(null); }
  }

  // Próximo premio: el más barato que todavía no alcanza.
  const catalog = (data.catalog || []).slice().sort((a, b) => a.points_required - b.points_required);
  const nextReward = catalog.find(c => c.points_required > points);
  const pct = nextReward ? Math.min(100, Math.round((points / nextReward.points_required) * 100)) : 0;

  // Entradas: próximas vs. pasadas/usadas.
  const tickets = data.tickets || [];
  const cupones = data.cupones || [];
  const isUpcoming = t => t.status !== 'usado' && t.status !== 'cancelado' && !isPast({ date: t.date });
  const upcoming = tickets.filter(isUpcoming);
  const past = tickets.filter(t => !isUpcoming(t));
  const history = data.history || [];

  const shortDate = d => { try { return new Date(d).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }); } catch { return ''; } };

  const ticketCard = (t, dim) => (
    <div key={t.qr_code} className="lb-card" style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, opacity: dim ? 0.55 : 1, borderLeft: '3px solid ' + (dim ? 'var(--border)' : 'var(--red-500)') }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: '#fff', fontWeight: 600 }}>{t.event_name}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-600)', textTransform: 'capitalize' }}>{fmtDate(t.date)} · {t.lot_name}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span className={'lb-status lb-status--dot lb-status--' + (t.status === 'usado' ? 'used' : 'paid')}>{t.status}</span>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setOpenTicket(t)}>Ver QR</button>
      </div>
    </div>
  );

  const emptyBox = txt => <div className="lb-card" style={{ padding: '22px', textAlign: 'center', color: 'var(--ink-600)', fontSize: 13 }}>{txt}</div>;

  return (
    <div className="lb-fade">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: '#fff' }}>Hola{firstName ? `, ${firstName}` : ''} 👋</div>
          <div style={{ color: 'var(--ink-600)', fontSize: 12 }}>{data.email}</div>
        </div>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={onLogout}>Salir</button>
      </div>

      {/* Puntos + progreso al próximo premio */}
      <div className="lb-card" style={{ padding: '20px 24px', marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="lb-eyebrow">Tus puntos</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 30, color: 'var(--red-400)' }}>{points}</div>
        </div>
        {nextReward ? (
          <div style={{ marginTop: 14 }}>
            <div style={{ height: 8, background: 'var(--ink-200)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: pct + '%', height: '100%', background: 'var(--red-500)', transition: 'width 400ms' }} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 8 }}>Te faltan <b style={{ color: '#fff' }}>{nextReward.points_required - points}</b> pts para <b style={{ color: '#fff' }}>{nextReward.name}</b></div>
          </div>
        ) : catalog.length ? (
          <div style={{ fontSize: 12, color: '#2ecc71', marginTop: 12 }}>¡Tenés puntos para canjear los premios disponibles! 🎉</div>
        ) : null}
        {history.length > 0 && (
          <>
            <button className="lb-link" style={{ marginTop: 14 }} onClick={() => setShowHist(s => !s)}>{showHist ? 'Ocultar movimientos' : 'Ver movimientos de puntos'}</button>
            {showHist && (
              <div style={{ marginTop: 10 }}>
                {history.map((h, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '8px 0', borderTop: i ? '1px solid rgba(255,255,255,0.06)' : 0 }}>
                    <span style={{ color: 'var(--ink-700)' }}>{h.concept || 'Movimiento'} · <span style={{ color: 'var(--ink-600)' }}>{shortDate(h.created_at)}</span>
                      {h.codigo && <span style={{ color: 'var(--red-300)', fontWeight: 600 }}> · código {h.codigo}</span>}</span>
                    <span style={{ color: h.points_added ? '#2ecc71' : 'var(--red-400)', fontWeight: 600 }}>{h.points_added ? `+${h.points_added}` : `-${h.points_spent}`}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Mesas: saldo de consumo (compartido con el grupo de la mesa) */}
      {(data.mesas || []).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Tus mesas</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.mesas.map(mm => (
              <div key={mm.public_code} className="lb-card" style={{ padding: '16px 18px', border: '1px solid rgba(236,38,143,0.35)', background: 'rgba(236,38,143,0.05)' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: '#fff', textTransform: 'uppercase' }}>{mm.sector_name ? `${mm.sector_name} · ` : ''}{mm.mesa_name}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 2 }}>{mm.event_name}</div>
                {mm.consumo_total > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF9DCD' }}>Saldo de consumo</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, color: '#fff', marginTop: 2 }}>{money(mm.consumo_remaining != null ? mm.consumo_remaining : mm.consumo_total)} <span style={{ fontSize: 12, color: 'var(--ink-600)', fontWeight: 400 }}>de {money(mm.consumo_total)}</span></div>
                    <div style={{ fontFamily: 'monospace', fontSize: 16, letterSpacing: '0.1em', color: '#FF9DCD', marginTop: 4 }}>{mm.consumo_code}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-700)', marginTop: 6 }}>Ingresá este código en la barra del sector para usar tu consumo.</div>
                  </div>
                )}
                <a className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginTop: 10, display: 'inline-block' }} href={`/mesa/${mm.public_code}`} target="_blank" rel="noreferrer">Ver mi mesa ↗</a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Próximas entradas */}
      <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Próximas entradas</div>
      {upcoming.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>{upcoming.map(t => ticketCard(t, false))}</div>
      ) : <div style={{ marginBottom: 24 }}>{emptyBox('No tenés entradas próximas. Cuando compres una, aparece acá con su QR.')}</div>}

      {/* Entradas pasadas / usadas */}
      {past.length > 0 && (
        <>
          <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Entradas pasadas</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>{past.map(t => ticketCard(t, true))}</div>
        </>
      )}

      {/* Cupones de la ruleta. El cupón llega por mail, pero el mail se pierde: acá
          está siempre, con el código a la vista y si ya se usó. */}
      {cupones.length > 0 && (
        <>
          <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Tus cupones</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
            {cupones.map(c => {
              const vencido = c.expires_at && new Date(c.expires_at) < new Date();
              const gastado = c.used || vencido;
              return (
                <div key={c.coupon_code} style={{ border: '1px solid var(--border)', borderRadius: 4, padding: '14px 16px', background: 'var(--ink-050)', opacity: gastado ? 0.55 : 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 15, color: '#fff', fontWeight: 600 }}>{c.prize}</div>
                    {c.used ? <span className="lb-status lb-status--used">Usado</span>
                      : vencido ? <span className="lb-status lb-status--gen">Vencido</span>
                        : <span className="lb-status lb-status--paid">Disponible</span>}
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: 20, color: 'var(--red-400)', letterSpacing: '0.12em', marginTop: 8 }}>{c.coupon_code}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 6 }}>
                    {c.event_name ? `Para ${c.event_name}` : 'Para tu próxima compra'}
                    {c.expires_at ? ` · vence el ${new Date(c.expires_at).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}` : ''}
                  </div>
                  {!gastado && c.event_id && (
                    <a className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginTop: 10, display: 'inline-block' }} href={`/e/${c.event_id}`}>Usarlo →</a>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Premios */}
      <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Premios para canjear</div>
      {catalog.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px,1fr))', gap: 12 }}>
          {catalog.map(c => {
            const ok = points >= c.points_required;
            return (
              <div key={c.id} className="lb-card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* La foto arriba: un premio con imagen se entiende sin leer. Cuadrada y
                    `cover`, igual que en la home — la foto ya se recorta cuadrada al subirla.
                    Acá la grilla es más densa a propósito: esto es la lista para elegir qué
                    canjear, conviene ver varios de una. */}
                <div style={{ position: 'relative', aspectRatio: '1 / 1', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                  {c.image_url
                    ? <img src={c.image_url} alt={c.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : <span style={{ fontSize: 30, opacity: 0.35 }}>🎁</span>}
                </div>
                <div style={{ padding: '16px 18px' }}>
                  <div style={{ color: 'var(--red-400)', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22 }}>{c.points_required} pts</div>
                  <div style={{ color: '#fff', fontWeight: 600, marginTop: 4 }}>{c.name}</div>
                  {c.description && <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 2 }}>{c.description}</div>}
                  {/* Qué le va a llegar: un cupón para usar online, o algo para retirar. */}
                  <div style={{ fontSize: 11, color: 'var(--ink-700)', marginTop: 6 }}>
                    {c.tipo === 'cupon'
                      ? `🎟️ Cupón ${c.descuento_pct || 100}% off · un solo uso${c.evento_nombre ? ` · para ${c.evento_nombre}` : ''}`
                      : `📦 ${c.retiro ? `Se retira en ${c.retiro}` : 'Se retira en el boliche'}`}
                  </div>
                  {/* Los talles/colores QUE QUEDAN. Los agotados no se listan: mostrar un
                      talle que no hay es prometerlo. */}
                  {/* Ahora las variantes se ELIGEN: la que toca queda marcada y es la que
                      viaja al canjear. Antes solo se listaban. */}
                  {Array.isArray(c.variants) && c.variants.filter(v => v.stock == null || v.stock > 0).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                      {c.variants.filter(v => v.stock == null || v.stock > 0).map(v => {
                        const elegida = variantePick[c.id] === v.label;
                        return (
                          <button key={v.label} type="button" onClick={() => { setVariantePick(p => ({ ...p, [c.id]: v.label })); setCanjeErr(null); }}
                            style={{ fontSize: 10.5, letterSpacing: '0.06em', padding: '3px 9px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                              border: '1px solid ' + (elegida ? 'var(--red-400)' : 'var(--border)'),
                              background: elegida ? 'rgba(200,16,46,0.14)' : 'transparent',
                              color: elegida ? '#fff' : 'var(--ink-800)' }}>{v.label}</button>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ fontSize: 11, marginTop: 8, color: ok ? '#2ecc71' : 'var(--ink-700)' }}>
                    {ok ? '✓ Podés canjearlo' : `Te faltan ${c.points_required - points} pts`}
                    <span style={{ color: 'var(--ink-700)' }}> · {c.unlimited || c.stock == null ? 'Sin límite' : `${c.stock} disp.`}</span>
                  </div>
                  {ok && (
                    <button className="lb-btn lb-btn--primary lb-btn--sm lb-btn--block" style={{ marginTop: 10 }}
                      disabled={canjeando === c.id} onClick={() => canjear(c)}>
                      {canjeando === c.id ? 'Canjeando…' : 'Canjear'}
                    </button>
                  )}
                  {canjeErr?.id === c.id && <div className="lb-err" style={{ marginTop: 8, fontSize: 11.5 }}>{canjeErr.msg}</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : emptyBox('Todavía no hay premios cargados. ¡Seguí sumando puntos! 🎁')}

      {/* Canje hecho: el código es lo único que importa acá, así que va grande. Queda
          también en el historial, para que no dependa de no cerrar esta ventana. */}
      {canjeado && (
        <div className="lb-modal-backdrop" onClick={() => setCanjeado(null)}>
          <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 380, padding: '28px 26px', textAlign: 'center' }}>
            <div style={{ fontSize: 34 }}>🎁</div>
            <div className="lb-h" style={{ fontSize: 20, marginTop: 6 }}>¡Premio canjeado!</div>
            <div style={{ color: 'var(--ink-700)', fontSize: 14, marginTop: 6 }}>{canjeado.premio}</div>
            <div className="lb-eyebrow" style={{ marginTop: 20 }}>{canjeado.tipo === 'cupon' ? 'Tu cupón' : 'Tu código'}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: canjeado.tipo === 'cupon' ? 32 : 40, letterSpacing: '0.12em', color: 'var(--red-400)', marginTop: 4, wordBreak: 'break-all' }}>{canjeado.codigo}</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-700)', marginTop: 14, lineHeight: 1.55 }}>
              {canjeado.tipo === 'cupon'
                ? <>Usalo al comprar tu entrada{canjeado.evento ? <> de <b style={{ color: '#fff' }}>{canjeado.evento}</b></> : null}, en <b style={{ color: '#fff' }}>Código de cupón</b>: te descuenta el <b style={{ color: '#fff' }}>{canjeado.descuento_pct}%</b>. Sirve una sola vez.</>
                : <>Mostrá este código para retirar tu premio{canjeado.retiro ? <> en <b style={{ color: '#fff' }}>{canjeado.retiro}</b></> : ' en el boliche'}.</>}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-700)', marginTop: 8 }}>Te quedan <b style={{ color: '#fff' }}>{canjeado.points}</b> puntos.</div>
            <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 10 }}>Lo vas a volver a encontrar en tus movimientos de puntos.</div>
            <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 20 }} onClick={() => setCanjeado(null)}>Listo</button>
          </div>
        </div>
      )}

      {/* Privacidad: supresión de datos (Ley 25.326) */}
      <div style={{ marginTop: 34, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="lb-eyebrow" style={{ marginBottom: 8 }}>Privacidad</div>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ color: 'var(--red-400)' }} disabled={deleting} onClick={deleteAccount}>{deleting ? 'Borrando…' : 'Borrar mi cuenta y mis datos'}</button>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 8, lineHeight: 1.5, maxWidth: 460 }}>Se borran tu cuenta, tus puntos y tus datos personales de este boliche. Tus entradas ya compradas <b style={{ color: 'var(--ink-800)' }}>siguen siendo válidas para entrar</b> (el QR no se anula). No afecta a otros boliches.</div>
      </div>

      {openTicket && (
        <div className="lb-modal-backdrop" onClick={() => setOpenTicket(null)}>
          <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360, padding: '24px', textAlign: 'center' }}>
            <div className="lb-h" style={{ fontSize: 18, marginBottom: 4 }}>{openTicket.event_name}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 16 }}>{openTicket.lot_name}</div>
            <TicketQR value={openTicket.qr_code} logo={data.logo_url} eventName={openTicket.event_name} date={openTicket.date} lotName={openTicket.lot_name} attendeeName={fullName(openTicket.buyer_name || data.name, openTicket.buyer_lastname)} filename={`${openTicket.event_name}-${openTicket.qr_code}.png`} />
          </div>
        </div>
      )}
    </div>
  );
}
